import type { FrontendAccessPolicy, PlatformEntryContext } from '@/types';

const ACTIVE_CONTOUR_STORAGE_KEY = 'moy-gorod:active-contour';

export const normalizeContour = (value: string | null | undefined): string | null => {
  const normalized = String(value || '').trim().toLowerCase();
  return /^[a-z0-9][a-z0-9-]{0,63}$/.test(normalized) ? normalized : null;
};

export type ActiveContourResolution = {
  selectedContour: string | null;
  requestedContour: string | null;
  requestState: 'absent' | 'allowed' | 'invalid' | 'forbidden';
  sanitizedSearch: string;
};

type ResolveActiveContourOptions = {
  search?: string | null;
  storedContour?: string | null;
  allowedContourIds: Array<string | null | undefined>;
  fallbackContour?: string | null;
};

type ActiveContourAuthorityKeyOptions = {
  entryKind?: string | null;
  allowedContourIds: Array<string | null | undefined>;
  selectedContour?: string | null;
};

type ActiveContourAuthoritySyncOptions = {
  entryContextReady: boolean;
  appliedAuthorityKey: string;
  expectedAuthorityKey: string;
  activeContour?: string | null;
  selectedContour?: string | null;
};

export function resolveAllowedEntryContourIds(
  entryContext: PlatformEntryContext | null | undefined,
  accessPolicy: FrontendAccessPolicy | null | undefined,
): string[] {
  const verticalWorkspaceTenantIds = Array.from(new Set(
    (entryContext?.current_workspace?.tenant_ids ?? [])
      .map(normalizeContour)
      .filter((tenantId): tenantId is string => Boolean(tenantId)),
  ));
  if (
    entryContext?.current_workspace?.kind === 'vertical'
    && verticalWorkspaceTenantIds.length > 1
  ) {
    // The vertical workspace is already the server-authorized multi-tenant
    // envelope. A city contour would silently narrow that envelope and must not
    // be inherited from URL or browser state.
    return [];
  }
  const entryContourIds = Array.from(new Set(
    (entryContext?.contours ?? [])
      .map((contour) => normalizeContour(contour.tenant_id))
      .filter((tenantId): tenantId is string => Boolean(tenantId)),
  ));
  if (accessPolicy?.access_mode !== 'explicit') return entryContourIds;
  const policyContourIds = new Set(
    (accessPolicy.tenant_scope_ids ?? [])
      .map(normalizeContour)
      .filter((tenantId): tenantId is string => Boolean(tenantId)),
  );
  return entryContourIds.filter((tenantId) => policyContourIds.has(tenantId));
}

export function resolveEntryContourFallback(
  entryContext: PlatformEntryContext | null | undefined,
  allowedContourIds: Array<string | null | undefined>,
  allowCurrentHostWorkspace: boolean,
): string | null {
  if (!entryContext) return null;
  const allowed = new Set(
    allowedContourIds
      .map(normalizeContour)
      .filter((tenantId): tenantId is string => Boolean(tenantId)),
  );
  const defaultContour = normalizeContour(entryContext.default_contour);
  const workspaceContourIds = (entryContext.current_workspace?.tenant_ids ?? [])
    .map(normalizeContour)
    .filter((tenantId): tenantId is string => Boolean(tenantId));
  if (entryContext.entry_kind === 'single_contour') {
    return [defaultContour, ...allowed]
      .filter((tenantId): tenantId is string => Boolean(tenantId))
      .find((tenantId) => allowed.has(tenantId)) ?? null;
  }
  if (allowCurrentHostWorkspace && entryContext.current_workspace?.kind === 'city') {
    return [...workspaceContourIds, defaultContour, ...allowed]
      .filter((tenantId): tenantId is string => Boolean(tenantId))
      .find((tenantId) => allowed.has(tenantId)) ?? null;
  }
  if (allowCurrentHostWorkspace && entryContext.current_workspace?.kind === 'region') {
    const regionContour = normalizeContour(
      entryContext.current_workspace.key.match(/^region:(.+)$/)?.[1],
    );
    if (
      regionContour
      && workspaceContourIds.includes(regionContour)
      && allowed.has(regionContour)
    ) {
      return regionContour;
    }
  }
  return null;
}

/**
 * Identify the authorization envelope that owns the mounted workspace.
 * Route-local query parameters (ticketId, filters, audit flags, and so on)
 * deliberately do not participate: changing them must not remount the shell.
 */
export function buildActiveContourAuthorityKey({
  entryKind,
  allowedContourIds,
  selectedContour,
}: ActiveContourAuthorityKeyOptions): string {
  const allowed = allowedContourIds
    .map(normalizeContour)
    .filter((value): value is string => Boolean(value));
  return [
    String(entryKind || 'loading'),
    allowed.join(','),
    normalizeContour(selectedContour) ?? '-',
  ].join('|');
}

export function isActiveContourAuthoritySynchronized({
  entryContextReady,
  appliedAuthorityKey,
  expectedAuthorityKey,
  activeContour,
  selectedContour,
}: ActiveContourAuthoritySyncOptions): boolean {
  return Boolean(
    entryContextReady
      && appliedAuthorityKey === expectedAuthorityKey
      && normalizeContour(activeContour) === normalizeContour(selectedContour),
  );
}

/**
 * Resolve the selected tenant without touching browser state.
 *
 * The URL is a request, never an authority. Only tenant ids present in the
 * server-provided entry context may replace the already validated stored
 * selection. The returned query is canonical and safe to put back into the
 * address bar before mounting working-route children.
 */
export function resolveActiveContour({
  search = '',
  storedContour,
  allowedContourIds,
  fallbackContour,
}: ResolveActiveContourOptions): ActiveContourResolution {
  const allowed = new Set(
    allowedContourIds
      .map(normalizeContour)
      .filter((value): value is string => Boolean(value)),
  );
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const hasRequest = params.has('contour');
  const requestedContour = hasRequest ? normalizeContour(params.get('contour')) : null;
  const stored = normalizeContour(storedContour);
  const fallback = normalizeContour(fallbackContour);
  const storedAllowed = stored && allowed.has(stored) ? stored : null;
  const fallbackAllowed = fallback && allowed.has(fallback) ? fallback : null;

  let requestState: ActiveContourResolution['requestState'] = 'absent';
  let selectedContour: string | null = storedAllowed ?? fallbackAllowed;
  if (hasRequest) {
    if (!requestedContour) {
      requestState = 'invalid';
    } else if (!allowed.has(requestedContour)) {
      requestState = 'forbidden';
    } else {
      requestState = 'allowed';
      selectedContour = requestedContour;
    }
  }

  params.delete('contour');
  if (selectedContour) params.set('contour', selectedContour);
  const normalizedSearch = params.toString();

  return {
    selectedContour,
    requestedContour,
    requestState,
    sanitizedSearch: normalizedSearch ? `?${normalizedSearch}` : '',
  };
}

export function isAllowedActiveContour(
  tenantId: string | null | undefined,
  allowedContourIds: Array<string | null | undefined>,
): boolean {
  const normalized = normalizeContour(tenantId);
  if (!normalized) return false;
  return allowedContourIds.some((value) => normalizeContour(value) === normalized);
}

export function readActiveContour(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    // API and SSE consumers deliberately read only this validated store. A URL
    // parameter is handled by OperatorLayout against entryContext first.
    return normalizeContour(window.sessionStorage?.getItem(ACTIVE_CONTOUR_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function selectActiveContour(tenantId: string): string | null {
  const normalized = normalizeContour(tenantId);
  if (typeof window === 'undefined' || !normalized) return null;
  try {
    window.sessionStorage?.setItem(ACTIVE_CONTOUR_STORAGE_KEY, normalized);
  } catch {
    return null;
  }
  window.dispatchEvent(new CustomEvent('active_contour_changed', { detail: normalized }));
  return normalized;
}

export function clearActiveContour(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage?.removeItem(ACTIVE_CONTOUR_STORAGE_KEY);
  } catch {
    // Storage may be unavailable in hardened/private browser contexts.
  }
  window.dispatchEvent(new CustomEvent('active_contour_changed', { detail: null }));
}

export function withActiveContour(route: string, tenantId: string): string {
  const normalized = normalizeContour(tenantId);
  if (!normalized) return route;
  const url = new URL(route, 'https://moy-gorod.su');
  url.searchParams.set('contour', normalized);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function isActiveContourHeaderEligible(url: string | undefined): boolean {
  const path = String(url || '').toLowerCase();
  if (!path.includes('/api/')) return false;
  const rootAccountAdminPath = /\/api\/users\/[1-9][0-9]*\/(?:access_grants|access-account\/(?:profile|password))(?:[?#]|$)/.test(path);
  return !(
    path.includes('/api/users/login')
    || path.includes('/api/users/register')
    || path.includes('/api/users/profile')
    || path.includes('/api/users/access_policy')
    || path.includes('/api/users/entry-context')
    || path.includes('/api/users/workspace-handoffs')
    || path.includes('/api/users/access-admin/')
    || path.includes('/api/users/access-requests')
    || rootAccountAdminPath
  );
}
